import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Package, Settings, Users, FileText, 
  Briefcase, BarChart3, ChevronLeft, ChevronRight, LogOut,
  Sun, Menu, X, UserCircle, Image, Heart, Building2, Newspaper, Calendar, UserPlus
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const AdminSidebar = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasPermission } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const menuItems = [
    { 
      path: '/admin', 
      icon: LayoutDashboard, 
      label: 'Dashboard',
      permission: 'dashboard'
    },
    { 
      path: '/admin/banners', 
      icon: Image, 
      label: 'Banners',
      permission: 'settings_view'
    },
    { 
      path: '/admin/products', 
      icon: Package, 
      label: 'Productos',
      permission: 'products_view'
    },
    { 
      path: '/admin/projects', 
      icon: Briefcase, 
      label: 'Proyectos',
      permission: 'projects_view'
    },
    { 
      path: '/admin/clients', 
      icon: Users, 
      label: 'Clientes',
      permission: 'clients_view'
    },
    { 
      path: '/admin/leads', 
      icon: UserPlus, 
      label: 'Interesados',
      permission: 'clients_view'
    },
    { 
      path: '/admin/analytics', 
      icon: BarChart3, 
      label: 'Analytics',
      permission: 'analytics_view'
    },
    { 
      path: '/admin/social-projects', 
      icon: Heart, 
      label: 'Acción Social',
      permission: 'settings_view'
    },
    { 
      path: '/admin/banks', 
      icon: Building2, 
      label: 'Bancos Aliados',
      permission: 'settings_view'
    },
    { 
      path: '/admin/news', 
      icon: Newspaper, 
      label: 'Noticias',
      permission: 'settings_view'
    },
    { 
      path: '/admin/events', 
      icon: Calendar, 
      label: 'Eventos',
      permission: 'settings_view'
    },
    { 
      path: '/admin/users', 
      icon: UserCircle, 
      label: 'Usuarios',
      permission: 'users_view'
    },
    { 
      path: '/admin/settings', 
      icon: Settings, 
      label: 'Configuración',
      permission: 'settings_view'
    },
  ];

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const NavContent = () => (
    <>
      {/* Logo */}
      <div className="p-4 border-b border-zinc-800">
        <Link to="/admin" className="flex items-center gap-3">
          <Sun className="w-8 h-8 text-emerald-400" strokeWidth={1.5} />
          {!collapsed && (
            <span className="text-xl font-bold text-white">ZENEX</span>
          )}
        </Link>
      </div>

      {/* Menu */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {menuItems.map((item) => {
          if (!hasPermission(item.permission)) return null;
          
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group
                ${isActive 
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                  : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
                }`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <Icon className={`w-5 h-5 ${collapsed ? 'mx-auto' : ''}`} />
              {!collapsed && <span className="font-medium">{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* User & Logout */}
      <div className="p-4 border-t border-zinc-800">
        {!collapsed && user && (
          <div className="mb-3 px-3 py-2 bg-zinc-800/50 rounded-lg">
            <p className="text-sm font-medium text-white truncate">{user.full_name}</p>
            <p className="text-xs text-zinc-500 truncate">{user.email}</p>
            <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${
              user.role === 'admin' 
                ? 'bg-emerald-500/20 text-emerald-400' 
                : 'bg-blue-500/20 text-blue-400'
            }`}>
              {user.role === 'admin' ? 'Administrador' : 'Empleado'}
            </span>
          </div>
        )}
        
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 w-full px-3 py-2.5 text-zinc-400 hover:bg-red-500/10 hover:text-red-400 rounded-lg transition-all"
          data-testid="logout-btn"
        >
          <LogOut className={`w-5 h-5 ${collapsed ? 'mx-auto' : ''}`} />
          {!collapsed && <span>Cerrar sesión</span>}
        </button>
      </div>

      {/* Collapse toggle (desktop) */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 bg-zinc-800 border border-zinc-700 rounded-full items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors"
      >
        {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
      </button>
    </>
  );

  return (
    <>
      {/* Mobile toggle */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-zinc-800 rounded-lg text-white"
      >
        {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:relative inset-y-0 left-0 z-40
        bg-zinc-900 border-r border-zinc-800
        transition-all duration-300 flex flex-col
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${collapsed ? 'w-20' : 'w-64'}
      `}>
        <NavContent />
      </aside>
    </>
  );
};

export default AdminSidebar;
